import React, { useContext, useState } from 'react';
import { Select } from 'src/components/Select/Select';
import { Input } from '@riac/design-system';
import { Form, SubmitButton, FlexRow, AddressTypeSelect, FlexRowButtons, CancelButton } from 'src/pages/AccountPage/components/AddressForm/AddressForm.style';
import { SuggestedAddressModal } from 'src/components/AddressValidation/SuggestedAddressModal';
import { IAddressForm } from 'src/pages/AccountPage/AddressBook/AddressBook.i';
import { addressValidation } from 'src/components/AddressValidation/addressValidation';
import { AccountPagesContext } from 'src/lib/AccountPagesContext';

export const AddressForm = ({ formData, addressTypeList, setFormAction, formAction }: IAddressForm) => {
  const { getAddresses, addresses, states, countries } = useContext(AccountPagesContext);
  const [address, setAddress] = useState(formData);
  const [promptSuggested, setPromptSuggested] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [suggestedAddress, setSuggestedAddress] = useState({
    sugAddr2: '',
    sugAddr1: '',
    sugCity: '',
    sugState: '',
    sugZip4: '',
    sugZip5: '',
  });

  function handleInput(name, value) {
    setAddress((prev) => ({ ...prev, [name]: value }));
  }

  function handleSelect(e, value: string, label?: string) {
    setAddress((prev) => ({ ...prev, [value]: e.value }));
    if(label) setAddress((prev) => ({ ...prev, [label]: e.label }));
  }

  function handleCancel() {
    setFormAction(null);
  }

  async function handleSubmit() {
    if(disableForm) return;
    setDisableForm(true);
    const { AddressID, Address, Address2, City, State, Zip, CountryID, Province, AddressTypeID } = address;

    // Validate US addresses
    if(CountryID === 236) {
      const validate = await addressValidation({ address1: Address, address2: Address2, city: City, state: State, zipCode: Zip });
      if(validate.success) {
        setSuggestedAddress(validate.address);
        setPromptSuggested(true);
        return;
      }
    }
    // Other country or validation failed-process with orignal inputs
    formAction === 'edit'
      ? updateAddress(AddressID, Address, Address2, City, State, Zip, CountryID, Province, AddressTypeID)
      : submitNewAddress(Address, Address2, City, State, Zip, CountryID, Province, AddressTypeID);
  }

  async function submitNewAddress(addr1, addr2, city, state, zip, country, province, typeId) {
    await fetch('/api/account/address/newAddress', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        address1: addr1,
        address2: addr2,
        city: city,
        state: state,
        zip: zip,
        country: country,
        province: province,
        addressTypeID: typeId,
      }),
    });
    setDisableForm(false);
    setPromptSuggested(false);
    setFormAction('done');
    getAddresses();
  }

  async function updateAddress(addrId, addr1, addr2, city, state, zip, country, province, typeId) {
    await fetch(`/api/account/address/editAddress/${addrId}`, {
      credentials: 'include',
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        address1: addr1,
        address2: addr2,
        city: city,
        state: state,
        zip: zip,
        country: country,
        province: province,
        addressTypeID: typeId,
      }),
    });
    setDisableForm(false);
    setPromptSuggested(false);
    setFormAction('done');
    getAddresses();
  }

  // Create options for address type dropdown
  const addressTypeOptions = [];
  // Add primary as only option if editing primary address or the only address
  if(address.AddressType === 'Primary' || addresses.length < 1) {
    addressTypeOptions.push({
      value: '1',
      label: 'Primary',
    });
  } else {
    // Fill in the rest of the options available for address type if not primary
    addressTypeList.map((type) => {
      addressTypeOptions.push({
        value: type.AddressTypeID,
        label: type.AddressType,
      });
    });
  }
  // Get state options
  const stateOptions = [];
  let stateName = null;
  states.map((state) => {
    stateOptions.push({
      value: state.Abbreviation,
      label: state.StateName,
    });
    if(address.State === state.Abbreviation) {
      stateName = state.StateName;
    }
  });
  // Get Countries
  const countryOptions = [];
  let countryName = null;
  countries.map((country) => {
    countryOptions.push({
      value: country.CountryID,
      label: country.CountryName,
    });
    if(country.CountryID === address.CountryID) {
      countryName = country.CountryName;
    }
  });

  return (
    <div>
      <Form>
        <FlexRow>
          <AddressTypeSelect
            label='Address Type'
            options={addressTypeOptions}
            onChange={(e) => handleSelect(e, 'AddressTypeID', 'AddressType')}
            name='AddressTypeID'
            value={{
              value: address.AddressTypeID.toString(),
              label: address.AddressType,
            }}
            disabled={disableForm}
            autoComplete = 'off'
            inverted
          />
        </FlexRow>
        <FlexRow>
          <Input
            label='Address 1'
            value={address.Address}
            name='Address'
            onChange={(val) => handleInput('Address', val)}
            disabled={disableForm}
            autoComplete = 'address-line1'
            inverted
          />
          <Input
            label='Address 2'
            value={address.Address2}
            name='Address2'
            onChange={(val) => handleInput('Address2', val)}
            disabled={disableForm}
            autoComplete = 'address-line2'
            inverted
          />
        </FlexRow>
        <FlexRow>
          <Input
            label='City'
            value={address.City}
            name='City'
            onChange={(val) => handleInput('City', val)}
            disabled={disableForm}
            autoComplete = 'address-level2'
            inverted
          />
          <Select
            label='State'
            options={stateOptions}
            onChange={(e) => handleSelect(e, 'State')}
            name='State'
            value={{
              value: address.State,
              label: stateName,
            }}
            disabled={disableForm}
            autoComplete = 'address-level3'
            inverted
          />
          <Input
            label='Province'
            value={address.Province}
            name='Province'
            onChange={(val) => handleInput('Province', val)}
            disabled={disableForm}
            autoComplete = 'address-level4'
            inverted
          />
        </FlexRow>
        <FlexRow>
          <Select
            label='Country'
            options={countryOptions}
            onChange={(e) => handleSelect(e, 'CountryID')}
            name='CountryID'
            value={{
              value: address.CountryID.toString(),
              label: countryName,
            }}
            disabled={disableForm}
            autoComplete = 'country-name'
            inverted
          />
          <Input
            label='Zip Code'
            value={address.Zip}
            name='Zip'
            onChange={(val) => handleInput('Zip', val)}
            disabled={disableForm}
            autoComplete = 'postal-code'
            inverted
          />
        </FlexRow>
        <FlexRowButtons>
          <SubmitButton isDisabled={disableForm} onPress = {() => {handleSubmit();}}>Submit</SubmitButton>
          <CancelButton onPress={() => handleCancel()}>Cancel</CancelButton>
        </FlexRowButtons>
      </Form>
      {promptSuggested
          && <SuggestedAddressModal
            original={{
              address1: address.Address,
              address2: address.Address2,
              city: address.City,
              state: address.State,
              zipCode: address.Zip,
            }}
            suggested={{
              sugAddr2: suggestedAddress.sugAddr2,
              sugAddr1: suggestedAddress.sugAddr1,
              sugCity: suggestedAddress.sugCity,
              sugState: suggestedAddress.sugState,
              sugZip4: suggestedAddress.sugZip4.toString(),
              sugZip5: suggestedAddress.sugZip5.toString(),
            }}
            selectOriginal={() => {
              const { AddressID, Address, Address2, City, State, Zip, CountryID, Province, AddressTypeID } = address;
              formAction === 'edit'
                ? updateAddress(AddressID, Address, Address2, City, State, Zip, CountryID, Province, AddressTypeID)
                : submitNewAddress(Address, Address2, City, State, Zip, CountryID, Province, AddressTypeID);
            }}
            selectSuggested={() => {
              const { AddressID, CountryID, Province, AddressTypeID } = address;
              const { sugAddr2, sugAddr1, sugCity, sugState, sugZip4, sugZip5 } = suggestedAddress;
              formAction === 'edit'
                ? updateAddress(AddressID, sugAddr1, sugAddr2, sugCity, sugState, (`${sugZip5}-${sugZip4}`), CountryID, Province, AddressTypeID)
                : submitNewAddress(sugAddr1, sugAddr2, sugCity, sugState, (`${sugZip5}-${sugZip4}`), CountryID, Province, AddressTypeID);
            }}
            cancel={() => {
              setDisableForm(false);
              setPromptSuggested(false);
            }}
          />
      }
    </div>
  );
};
